import type { NHovBranchLevels, NHovEffectBoundaryCaps } from "../types";
import {
  compareHuge,
  huge,
  hugeOne,
  minHuge,
  type HugeNumber,
} from "@/shared/numbers";
import { NUMBER_LIMIT_BOUNDARY } from "@/features/hoverIdle/core/numberLimitBoundary";
import {
  calculateHgAmplifyToHngGainMultiplierHuge,
  calculateHgDiscountToHngCostDivisorHuge,
  calculateHgNHovEffectsToHngTransferMultiplierHuge,
} from "./hgNHovEffectsToHngLogic";
import type { NHovBranchContinuousLevels } from "./nHovGrowthLogic";
import { resolveHovCellRankScaleBaseHuge } from "./hngScaleLogic";
import { calculateNHovHgAmplifyMultiplierHuge } from "./hgAmplifyLogic";
import { calculateNHovHgCostDivisorHuge } from "./hgDiscountLogic";
import { interpolateNHovEffectAtContinuousLevel } from "./nHovContinuousProgress";
import type { NHovBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export interface NHovBranchEffects {
  readonly hgAmplifyMultiplier: HugeNumber;
  readonly hgDiscountCostDivisor: HugeNumber;
  readonly hngRankScaleBase: HugeNumber;
  readonly hgNHovEffectsToHngTransferMultiplier: HugeNumber;
  readonly hgAmplifyToHngGainMultiplier: HugeNumber;
  readonly hgDiscountToHngCostDivisor: HugeNumber;
}

// Additive bonuses from Hov skills applied to each affected branch's growth
// exponent. NHov2 (`hgDiscount`) is intentionally absent: it stays the
// baseline branch with growth coefficient 1.
export interface NHovBranchGrowthExponentBonus {
  readonly hgAmplify: number;
  readonly hgNHovEffectsToHng: number;
  readonly hngScale: number;
}

export interface ResolveNHovBranchEffectsOptions {
  readonly branches: NHovBranchLevels;
  readonly balance?: NHovBalance;
  // Additive bonus from Hov skills applied to the HnG rank scale base before
  // the hngScale branch multiplier. Optional so callers without skill context
  // (simulation, isolated tests) keep returning the pre-skill base.
  readonly hngRankScaleBaseBonus?: number;
  // Additive bonus from Hov skills applied to the affected branches' growth
  // exponents. Optional so callers without skill context stay neutral.
  readonly growthExponentBonus?: NHovBranchGrowthExponentBonus;
  // When true (granted by `outside-nhov`), branch effects are no longer clamped
  // at NUMBER_LIMIT_BOUNDARY and grow as HugeNumber.
  readonly uncapped?: boolean;
  // Continuous branch levels used only to detect the exact NUMBER_LIMIT_BOUNDARY
  // crossing for the hgDiscountCostDivisor branch. They never change sub-cap
  // branch effects and are intentionally not generalized to other branches.
  readonly numberLimitCandidateLevels?: NHovBranchContinuousLevels;
  // Present for branches whose saved experience represents continuous
  // freshness-adjusted progress. Unmarked branches retain the wall-external
  // integer-level effect contract.
  readonly effectLevels?: NHovBranchContinuousLevels;
  // Used only by the hgDiscount boundary re-execution resolver, where the
  // next divisor is already resolved from a fresh-only integer level.
  readonly hgDiscountCostDivisorOverride?: HugeNumber;
  readonly effectBoundaryCaps?: NHovEffectBoundaryCaps;
}

export function resolveNHovBranchEffects(
  options: ResolveNHovBranchEffectsOptions,
): NHovBranchEffects {
  const { branches } = options;
  const growthExponentBonus = options.growthExponentBonus;
  const uncapped = options.uncapped === true;
  const effectLevels = options.effectLevels ?? branches;
  const candidates = uncapped ? undefined : options.numberLimitCandidateLevels;
  const candidateHgDiscountCostDivisor =
    candidates === undefined
      ? undefined
      : calculateNHovHgCostDivisorHuge(
          candidates.hgDiscount,
          options.balance?.branches.hgDiscount,
        );

  const normalHgAmplifyMultiplier = interpolateNHovEffectAtContinuousLevel({
    level: effectLevels.hgAmplify,
    resolveEffectAtIntegerLevel: (level) =>
      toPositiveBranchMultiplier(
        calculateNHovHgAmplifyMultiplierHuge(
          level,
          growthExponentBonus?.hgAmplify,
          options.balance,
        ),
        uncapped,
      ),
  });
  const normalHgDiscountCostDivisor =
    options.hgDiscountCostDivisorOverride ??
    interpolateNHovEffectAtContinuousLevel({
      level: effectLevels.hgDiscount,
      resolveEffectAtIntegerLevel: (level) =>
        toHgDiscountCostDivisor(
          calculateNHovHgCostDivisorHuge(
            level,
            options.balance?.branches.hgDiscount,
          ),
          uncapped,
          candidateHgDiscountCostDivisor,
          options.effectBoundaryCaps,
        ),
    });
  const normalHngRankScaleBase = interpolateNHovEffectAtContinuousLevel({
    level: effectLevels.hngScale,
    resolveEffectAtIntegerLevel: (level) =>
      toPositiveBranchMultiplier(
        resolveHovCellRankScaleBaseHuge({
          hngScaleLevel: level,
          hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
          exponentBonus: growthExponentBonus?.hngScale,
          balance: options.balance?.branches.hngScale,
        }),
        uncapped,
      ),
  });
  const normalHgNHovEffectsToHngTransferMultiplier =
    interpolateNHovEffectAtContinuousLevel({
      level: effectLevels.hgNHovEffectsToHng,
      additiveIdentityOffset: 1,
      resolveEffectAtIntegerLevel: (level) =>
        toNonNegativeBranchScalar(
          calculateHgNHovEffectsToHngTransferMultiplierHuge(
            level,
            growthExponentBonus?.hgNHovEffectsToHng,
            options.balance?.branches.hgNHovEffectsToHng,
          ),
          uncapped,
        ),
    });
  const hgAmplifyMultiplier = normalHgAmplifyMultiplier;
  const hgDiscountCostDivisor = normalHgDiscountCostDivisor;
  const hngRankScaleBase = normalHngRankScaleBase;
  const hgNHovEffectsToHngTransferMultiplier =
    normalHgNHovEffectsToHngTransferMultiplier;
  const hgAmplifyToHngGainMultiplier = toPositiveBranchMultiplier(
    calculateHgAmplifyToHngGainMultiplierHuge({
      level: effectLevels.hgNHovEffectsToHng,
      hgAmplifyMultiplier,
      exponentBonus: growthExponentBonus?.hgNHovEffectsToHng,
      transferMultiplierOverride: hgNHovEffectsToHngTransferMultiplier,
      balance: options.balance?.branches.hgNHovEffectsToHng,
    }),
    uncapped,
  );
  const hgDiscountToHngCostDivisor = toPositiveBranchMultiplier(
    calculateHgDiscountToHngCostDivisorHuge({
      hgDiscountCostDivisor,
      hgNHovEffectsToHngTransferMultiplier,
    }),
    uncapped,
  );

  return {
    hgAmplifyMultiplier,
    hgDiscountCostDivisor,
    hngRankScaleBase,
    hgNHovEffectsToHngTransferMultiplier,
    hgAmplifyToHngGainMultiplier,
    hgDiscountToHngCostDivisor,
  };
}

export function doesNHovHgDiscountCostDivisorReachNumberLimit(
  value: HugeNumber,
): boolean {
  if (!value.isFinite()) return true;
  return compareHuge(value, NUMBER_LIMIT_BOUNDARY) >= 0;
}

function capBranchEffectWithCandidate(
  value: HugeNumber,
  uncapped: boolean,
  numberLimitCandidate: HugeNumber | undefined,
): HugeNumber {
  if (uncapped) return value;
  if (reachesNumberLimitCandidate(numberLimitCandidate)) {
    return NUMBER_LIMIT_BOUNDARY;
  }
  return minHuge(value, NUMBER_LIMIT_BOUNDARY);
}

function reachesNumberLimitCandidate(
  candidate: HugeNumber | undefined,
): boolean {
  if (candidate === undefined) return false;
  if (!candidate.isFinite()) return true;
  return compareHuge(candidate, NUMBER_LIMIT_BOUNDARY) >= 0;
}

function toPositiveBranchMultiplier(
  value: HugeNumber,
  uncapped: boolean,
  numberLimitCandidate?: HugeNumber,
): HugeNumber {
  if (!value.isFinite()) {
    return uncapped ? value : NUMBER_LIMIT_BOUNDARY;
  }
  if (compareHuge(value, 0) <= 0) return hugeOne();
  return capBranchEffectWithCandidate(value, uncapped, numberLimitCandidate);
}

function toHgDiscountCostDivisor(
  value: HugeNumber,
  uncapped: boolean,
  numberLimitCandidate: HugeNumber | undefined,
  effectBoundaryCaps: NHovEffectBoundaryCaps | undefined,
): HugeNumber {
  if (effectBoundaryCaps?.hgDiscountCostDivisor === true) {
    return NUMBER_LIMIT_BOUNDARY;
  }
  return toPositiveBranchMultiplier(value, uncapped, numberLimitCandidate);
}

function toNonNegativeBranchScalar(
  value: HugeNumber,
  uncapped: boolean,
  numberLimitCandidate?: HugeNumber,
): HugeNumber {
  if (!value.isFinite()) {
    return uncapped ? value : NUMBER_LIMIT_BOUNDARY;
  }
  if (compareHuge(value, 0) <= 0) return huge(0);
  return capBranchEffectWithCandidate(value, uncapped, numberLimitCandidate);
}
